import { useContext } from 'react'
import { checkboxSizeOptions } from '../../../atoms/checkboxes/constants'
import CheckboxComponent from '../../../atoms/checkboxes'
import { ChevronRight, Confirm } from '../../../atoms/icons/essentials'
import { iconSizeOptions } from '../../../atoms/icons/icons-sizes'
import RadioComponent from '../../../atoms/radio'
import {
  contextMenuControlOptions,
  contextMenuPaddingOptions,
  contextMenuTitleSizeOptions,
  contextMenuTitleWeightOptions,
  contextMenuVariantOptions,
  skeletonLoading
} from '../constants'
import {
  Container,
  ItemContainer,
  ItemContainerDestructive,
  ItemContainerRestricted,
  Loading,
  SecondaryText,
  TextContainer,
  Title
} from '../styled-components'
import PropTypes from 'prop-types'
import { ContextMenuContext } from './context-menu-context'
import colorsOptions from '../../../fundamentals/colors-styles/constants'

const ContextMenuItemComponents = {
  [contextMenuVariantOptions.STANDARD]: ItemContainer,
  [contextMenuVariantOptions.DESTRUCTIVE]: ItemContainerDestructive,
  [contextMenuVariantOptions.RESTRICTED]: ItemContainerRestricted
}

const ContextMenuItem = (props) => {
  const {
    padding = contextMenuPaddingOptions.LARGE,
    variant = contextMenuVariantOptions.STANDARD,
    title = null,
    titleWeight = contextMenuTitleWeightOptions['TEXT-500'],
    titleSize = contextMenuTitleSizeOptions['TEXT-R'],
    titleColor = colorsOptions.greys.black,
    leadingIcon = null,
    trailingIcon = null,
    badge = null,
    chevron = null,
    secondaryText = null,
    id = null,
    ...rest
  } = props

  if (id == null || typeof id !== 'string') {
    throw new Error(
      'The `id` prop is required for ContextMenuItem and must be a string.'
    )
  }

  const {
    size,
    control,
    handleAddItem,
    handleRemoveItem,
    handleCheckboxChange,
    noEmptyField,
    selectedItems,
    checkBoxProps,
    isLoading,
    handleRadioChange
  } = useContext(ContextMenuContext)

  const isItemSelected = selectedItems.some((item) => item.id === id)

  const handleClick = (item) => {
    if (noEmptyField && isItemSelected && selectedItems.length > 0) return

    if (isItemSelected) {
      handleRemoveItem(item)
    } else {
      handleAddItem(item)
    }
  }

  const HandleControls = () => {
    if (control === contextMenuControlOptions.CHECKBOX) {
      return (
        <CheckboxComponent.Root
          size={checkboxSizeOptions.MEDIUM}
          checked={isItemSelected}
          onCheckedChange={() => handleCheckboxChange(props, isItemSelected)}
          {...checkBoxProps}
        >
          <CheckboxComponent.Indicator>
            <Confirm size={iconSizeOptions['ICON-S']} />
          </CheckboxComponent.Indicator>
        </CheckboxComponent.Root>
      )
    } else if (control === contextMenuControlOptions.RADIO) {
      return (
        <RadioComponent.Item
          value={id}
          customOnCheck={() => {
            handleRadioChange(props)
          }}
        >
          <RadioComponent.Indicator />
        </RadioComponent.Item>
      )
    }

    return null
  }

  const Component = ContextMenuItemComponents[variant] || ItemContainer

  if (isLoading) {
    return <Loading padding={padding} {...skeletonLoading} />
  }

  return (
    <Component
      padding={padding}
      size={size}
      variant={variant}
      onClick={(e) => {
        e.stopPropagation()
        if (!control) {
          handleClick(props)
        }
      }}
      data-key={id}
      data-selected={isItemSelected}
      {...rest}
    >
      <Container>
        <HandleControls />
        {leadingIcon && leadingIcon}
        <TextContainer>
          <Title
            weight={titleWeight}
            size={titleSize}
            isItemSelected={isItemSelected}
            color={titleColor}
          >
            {title}
          </Title>
          {secondaryText && <SecondaryText>{secondaryText}</SecondaryText>}
        </TextContainer>
      </Container>

      <Container>
        {badge && badge}
        {trailingIcon && trailingIcon}
        {chevron && <ChevronRight size={iconSizeOptions['ICON-S']} />}
        {!control && isItemSelected && (
          <Confirm size={iconSizeOptions['ICON-S']} strokeWidth={4} />
        )}
      </Container>
    </Component>
  )
}

ContextMenuItem.propTypes = {
  padding: PropTypes.oneOf(Object.values(contextMenuPaddingOptions)),
  variant: PropTypes.oneOf(Object.values(contextMenuVariantOptions)),
  title: PropTypes.string,
  titleWeight: PropTypes.oneOf(Object.values(contextMenuTitleWeightOptions)),
  titleSize: PropTypes.oneOf(Object.values(contextMenuTitleSizeOptions)),
  titleColor: PropTypes.string,
  leadingIcon: PropTypes.node,
  trailingIcon: PropTypes.node,
  chevron: PropTypes.bool,
  secondaryText: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  badge: PropTypes.node
}

export default ContextMenuItem
